import React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsx mdx */

import Layout from '../../contents/sequenceDiagram/Layout';
import { Link } from 'gatsby';
import SEO from '../../components/seo';
export const _frontmatter = {};

const makeShortcode = name => function MDXDefaultShortcode(props) {
  console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope");
  return <div {...props} />;
};

const layoutProps = {
  _frontmatter
};
const MDXLayout = Layout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">

    <SEO title="UML Sequence Diagram Tutorial" description="Complete guide to learn everything their is to know about UML Sequence Diagrams with examples." mdxType="SEO" />
    <h1>{`UML Sequence diagram `}<em parentName="h1">{`Tutorial`}</em></h1>
    <p>{`Sequence diagrams are part of the Unified Modelling Language (UML) that aims to standardize visual
representation of systems design. It is the most popular interaction diagram in the field of software engineering.`}</p>
    <h2>{`What is a Sequence diagram?`}</h2>
    <p>{`In UML, a sequence diagram is a visual representation of the interaction between two or more systems.
It more specifically shows the messages the systems exchange, and in what order. Sequence diagrams may also be
called `}<em parentName="p">{`event diagrams`}</em>{` or `}<em parentName="p">{`event scenarios`}</em>{`.`}</p>
    <h2>{`Purpose of Sequence diagrams`}</h2>
    <ul>
      <li parentName="ul">{`Model high-level interaction between multiple systems`}</li>
      <li parentName="ul">{`Visualize complex procedures`}</li>
      <li parentName="ul">{`Model complex API call sequences`}</li>
      <li parentName="ul">{`Explain how a chain of events impacts a system`}</li>
      <li parentName="ul">{`Understand how multiple services interact in a micro-services architecture`}</li>
    </ul>
    <h2>{`How to read a Sequence diagram?`}</h2>
    <p>{`The horizontal axis of a Sequence diagram shows the systems that are involved in the interaction and the vertical axis
represents time.`}</p>
    <p>{`The diagram should be read from top (older interactions) to bottom (most recent interactions).`}</p>
    <p>{`The vertical axis, or time axis, is not representative of the duration of the interaction, it only specifies the order
in which the interactions take place.`}</p>
    <h2>{`Benefits of Sequence diagrams`}</h2>
    <ul>
      <li parentName="ul">{`Sequence diagrams are language neutral`}</li>
      <li parentName="ul">{`They are a common language that coders and non-coders understand`}</li>
      <li parentName="ul">{`UML Sequence diagrams facilitate communication within a team and accross multiple teams`}</li>
    </ul>
    <hr></hr>
    <h1>{`Sequence diagram `}<em parentName="h1">{`examples`}</em></h1>
    <p>{`The following Sequence diagram is directly taken from Google's documentation on OAuth 2.0 and shows how to
integrate OAuth to your app:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-sequence",
        "metastring": "graphOnly",
        "graphOnly": true
      }}>{`participant "Your app" as y
participant "Google servers" as g

y->g: Request token
activate y
activate g
box over g: User login & consent
g-->y: Authorization code
deactivate g
y->g: Exchange code for token
activate g
g-->y: Token response
deactivate g
y->g: Use token to call Google API
activate g
`}</code></pre>
    <hr></hr>
    <h1>{`Sequence diagram `}<em parentName="h1">{`notation`}</em></h1>
    <h2>{`Lifeline`}</h2>
    <p>{`A lifeline represent a participant in the interaction. It is visualized by a vertical dashed line with the
participant's name at the top. Lifelines are spread horizontally across the diagram.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-sequence",
        "metastring": "graphOnly",
        "graphOnly": true
      }}>{`participant Service A
`}</code></pre>
    <h2>{`Messages`}</h2>
    <p>{`Messages are depicted using arrows, they represent an interaction between two participants.`}</p>
    <p>{`Synchronous messages use a solid arrow head, they are used when the sender has to wait for the receiver
to send a response to continue.
The response message uses a dashed line as shown in the example bellow.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-sequence",
        "metastring": "graphOnly",
        "graphOnly": true
      }}>{`participant "Alice" as a
participant "Bob" as b

a->b: Message
b-->a: Return message
`}</code></pre>
    <p>{`Asynchronous messages use a lined arrow head, they do not require a response from the receiver to continue with the
interaction.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-sequence",
        "metastring": "graphOnly",
        "graphOnly": true
      }}>{`Alice->>Bob: Asynchronous message
`}</code></pre>
    <p>{`In some scenarios a participant might need to send a message to itself, those messages are called self-messages and
are represented by a U shaped arrow.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-sequence",
        "metastring": "graphOnly",
        "graphOnly": true
      }}>{`Alice->Alice: Self message
`}</code></pre>
    <p>{`Sometime we want to highlight messages latency. Duration messages are messages that take some time to transit.
Duration messages are shown using a tilted arrow.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-sequence",
        "metastring": "graphOnly",
        "graphOnly": true
      }}>{`participant "Alice" as a
participant "Bob" as b

a->(1)b: Slow
b-->(4)a: Slower
`}</code></pre>
    <h2>{`Activations`}</h2>
    <p>{`A thin rectangle over a lifeline represent the time a participant is active and performing an operation.
The top of the rectangle is the beginning of the operation and the bottom the end.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-sequence",
        "metastring": "graphOnly",
        "graphOnly": true
      }}>{`participant "Alice" as a
participant "Bob" as b

a->b: Hello
activate b
b-->a: Hi!
deactivate b
`}</code></pre>
    <hr></hr>
    <h1>{`How to make a sequence diagram?`}</h1>
    <p>{`You can use any drawing application to draw simple shapes and create a sequence diagram, or you can use a dedicated tool
like `}<strong parentName="p">{`GraphUp`}</strong>{` to build any type of sequence diagram without having to drag-n-drop shapes, resize,
and align things yourself.`}</p>
    <p>{`The benefits of using a tool like `}<strong parentName="p">{`GraphUp`}</strong>{` also include the ability to share and integrate your sequence diagrams
in one click across many platforms including Notion, GitHub, Jira, and Confluence.`}</p>
    <Link className="button primary" to="/sequence-diagram" mdxType="Link">
    Start building now!
    </Link>


    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      